/**
 * Progress of a visit through its SCHEDULED window. The DTO carries no actual
 * encounter start, so this measures from the booked start — it answers "how far
 * into the booked hour are we", not "how long has the clinician been in the room".
 */
export interface VisitElapsed {
  elapsedMinutes: number;
  /** Minutes left in the booked window; 0 once it is spent. */
  remainingMinutes: number;
  /** 0–100, capped so the bar has somewhere to stop when the visit runs long. */
  percent: number;
  overrun: boolean;
  /** Minutes past the scheduled end. Only set when `overrun`. */
  overMinutes?: number;
  /**
   * The scheduled start is still ahead — an early check-in. The encounter is
   * under way but the booked window has not opened, so the block reports the
   * wait rather than going silent on timing altogether.
   */
  pending: boolean;
  /** Minutes until the scheduled start. Only set when `pending`. */
  startsInMinutes?: number;
}

export function projectVisitElapsed({
  start,
  end,
  now,
}: {
  start?: string;
  end?: string;
  now: Date;
}): VisitElapsed | null {
  if (!start || !end) return null;
  const from = new Date(start).getTime();
  const to = new Date(end).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return null;

  const elapsedMs = now.getTime() - from;
  const windowMs = Math.max(0, to - from);

  if (elapsedMs < 0) {
    return {
      elapsedMinutes: 0,
      remainingMinutes: Math.round(windowMs / 60_000),
      percent: 0,
      overrun: false,
      pending: true,
      startsInMinutes: Math.ceil(-elapsedMs / 60_000),
    };
  }

  const overMs = now.getTime() - to;
  const overrun = overMs > 0;

  return {
    elapsedMinutes: Math.floor(elapsedMs / 60_000),
    remainingMinutes: Math.max(0, Math.ceil(-overMs / 60_000)),
    percent: windowMs === 0 ? 100 : Math.min(100, Math.round((elapsedMs / windowMs) * 100)),
    overrun,
    pending: false,
    ...(overrun ? { overMinutes: Math.floor(overMs / 60_000) } : {}),
  };
}
